<template>
  <div v-if="isMobileViewport" class="widget-embed-custom widget-embed-custom--mobile">
    <button type="button" class="widget-embed-custom__mobile-trigger" @click="openMobilePreview">
      <span class="widget-embed-custom__mobile-icon"><AppIcon icon="box-arrow-up-right"></AppIcon></span>
      <span class="widget-embed-custom__mobile-copy">
        <small>Vista optimizada para celular</small>
        <strong>Abrir contenido completo</strong>
        <span>Para visualizarlo correctamente, tocá acá para abrirlo en pantalla completa.</span>
        <em v-if="isExternalContent">El contenido es externo: si incluye tablas, podés girar el teléfono para ver más columnas.</em>
      </span>
      <AppIcon icon="chevron-right" class="widget-embed-custom__mobile-chevron"></AppIcon>
    </button>

    <b-modal v-model="mobilePreviewOpen"
             hide-footer
             scrollable
             dialog-class="widget-embed-custom__mobile-dialog"
             content-class="widget-embed-custom__mobile-modal-content"
             body-class="widget-embed-custom__mobile-modal-body"
             @hidden="loaded = false">
      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon icon="box-arrow-up-right"></AppIcon></span>
        <h5 class="app-modal-header__title">{{ title || 'Contenido del widget' }}</h5>
        <button type="button" class="close" aria-label="Cerrar" @click="mobilePreviewOpen = false"><span aria-hidden="true">&times;</span></button>
      </div>
      <div v-if="subtitle" class="widget-embed-custom__mobile-context">
        <AppIcon icon="info-circle"></AppIcon>
        <span>{{ subtitle }}</span>
      </div>
      <div class="widget-embed-custom__mobile-frame">
        <div class="widget-embed-custom__loading" v-if="!loaded">
          <b-spinner variant="primary" small label="Cargando"></b-spinner>
        </div>
        <iframe v-if="mobilePreviewOpen"
                :src="data"
                allowfullscreen
                loading="lazy"
                frameborder="0"
                :title="title || 'Contenido del widget'"
                class="widget-embed-custom__iframe"
                @load="loaded = true"
        ></iframe>
      </div>
    </b-modal>
  </div>

  <div v-else class="widget-embed-custom" :style="{ height: heightValue }">
    <div class="widget-embed-custom__loading" v-if="!loaded">
      <b-spinner variant="primary" small label="Cargando"></b-spinner>
    </div>
    <iframe
      :src="data"
      allowfullscreen
      loading="lazy"
      frameborder="0"
      class="widget-embed-custom__iframe"
      @load="loaded = true"
    ></iframe>
  </div>
</template>

<script>
  export default {
    props: {
      data: {
        type: String,
        required: true
      },
      height: {
        type: Number,
        default: 0
      },
      title: {
        type: String,
        default: ''
      },
      subtitle: {
        type: String,
        default: ''
      },
    },
    data() {
      return {
        loaded: false,
        mobilePreviewOpen: false,
        isMobileViewport: false,
      }
    },
    computed: {
      heightValue() {
        return this.height > 0 ? this.height + 'px' : '100%'
      },
      isExternalContent() {
        try {
          return new URL(this.data, window.location.href).origin !== window.location.origin
        } catch (error) {
          return true
        }
      }
    },
    watch: {
      data() {
        this.loaded = false
      },
    },
    mounted() {
      this.updateViewport()
      window.addEventListener('resize', this.updateViewport)
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.updateViewport)
    },
    methods: {
      updateViewport() {
        this.isMobileViewport = window.innerWidth <= 767
        if (!this.isMobileViewport) this.mobilePreviewOpen = false
      },
      openMobilePreview() {
        this.loaded = false
        this.mobilePreviewOpen = true
      },
    },
  }
</script>
<style scoped>
  .widget-embed-custom {
    position: relative;
  }
  .widget-embed-custom__iframe {
    border: 0;
    display: block;
    height: 100%;
    width: 100%;
  }
  .widget-embed-custom__loading {
    align-items: center;
    background: var(--app-color-surface);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
  .widget-embed-custom--mobile {
    display: flex;
    padding: 12px;
  }
  .widget-embed-custom__mobile-trigger {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
    padding: 12px;
    overflow: hidden;
    border: 1px solid var(--app-color-primary-200);
    border-radius: 10px;
    background: linear-gradient(110deg, var(--app-color-primary-soft) 0%, var(--app-color-surface) 72%);
    color: var(--app-color-text-secondary);
    text-align: left;
    transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease;
  }
  .widget-embed-custom__mobile-trigger:hover,
  .widget-embed-custom__mobile-trigger:focus {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-primary-soft-hover);
    outline: 0;
    transform: translateY(-1px);
  }
  .widget-embed-custom__mobile-trigger::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--app-color-primary);
    content: '';
  }
  .widget-embed-custom__mobile-icon {
    display: inline-flex;
    flex: 0 0 34px;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: var(--app-color-primary);
    color: var(--app-color-on-primary);
  }
  .widget-embed-custom__mobile-copy {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
  }
  .widget-embed-custom__mobile-copy small {
    color: var(--app-color-primary);
    font-size: 0.62rem;
    font-weight: var(--app-font-bold);
    letter-spacing: 0.06em;
    line-height: 1.2;
    text-transform: uppercase;
  }
  .widget-embed-custom__mobile-copy strong {
    color: var(--app-color-primary-active);
    font-size: var(--app-text-sm);
  }
  .widget-embed-custom__mobile-copy span {
    font-size: var(--app-text-xs);
    line-height: 1.35;
  }
  .widget-embed-custom__mobile-copy em {
    margin-top: 3px;
    color: var(--app-color-text-muted);
    font-size: 0.66rem;
    font-style: normal;
    line-height: 1.3;
  }
  .widget-embed-custom__mobile-chevron {
    flex: 0 0 auto;
    color: var(--app-color-primary);
  }
  .widget-embed-custom__mobile-context {
    display: flex;
    flex: 0 0 auto;
    align-items: flex-start;
    gap: 7px;
    margin: 10px 12px 0;
    padding: 9px 10px;
    border: 1px solid var(--app-color-primary-100);
    border-radius: 8px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-xs);
    line-height: 1.4;
  }
  .widget-embed-custom__mobile-context .app-icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: var(--app-color-primary);
  }
  .widget-embed-custom__mobile-context span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .widget-embed-custom__mobile-frame {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
  }
</style>
<style>
  @media (max-width: 767.98px) {
    .widget-embed-custom__mobile-dialog {
      width: calc(100% - 16px);
      max-width: none;
      height: calc(100% - 16px);
      margin: 8px auto;
    }
    .widget-embed-custom__mobile-modal-content {
      height: 100%;
      border: 1px solid var(--app-color-border);
    }
    .widget-embed-custom__mobile-modal-body {
      display: flex;
      flex-direction: column;
      min-height: 0;
      padding: 0;
      overflow: hidden;
    }
  }
</style>
